// ============================================================================
// Style Constants — Fonts, Colors, Animations, Presets
// ============================================================================

export const FONT_FAMILIES = [
  { value: 'system-ui', label: 'System', preview: 'system-ui, -apple-system, sans-serif' },
  { value: 'Inter', label: 'Inter', preview: 'Inter, sans-serif' },
  { value: 'Roboto', label: 'Roboto', preview: 'Roboto, sans-serif' },
  { value: 'Open Sans', label: 'Open Sans', preview: '"Open Sans", sans-serif' },
  { value: 'Lato', label: 'Lato', preview: 'Lato, sans-serif' },
  { value: 'Poppins', label: 'Poppins', preview: 'Poppins, sans-serif' },
  { value: 'Montserrat', label: 'Montserrat', preview: 'Montserrat, sans-serif' },
  { value: 'Source Sans 3', label: 'Source Sans', preview: '"Source Sans 3", sans-serif' },
  { value: 'Georgia', label: 'Georgia', preview: 'Georgia, serif' },
  { value: 'Playfair Display', label: 'Playfair Display', preview: '"Playfair Display", serif' },
  { value: 'Merriweather', label: 'Merriweather', preview: 'Merriweather, serif' },
  { value: 'Fira Code', label: 'Fira Code', preview: '"Fira Code", monospace' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', preview: '"JetBrains Mono", monospace' },
  { value: 'Nunito', label: 'Nunito', preview: 'Nunito, sans-serif' },
  { value: 'Raleway', label: 'Raleway', preview: 'Raleway, sans-serif' },
];

export const SYSTEM_FONTS = ['system-ui', 'Georgia'];

export const FONT_WEIGHTS = [
  { value: 100, label: 'Thin' },
  { value: 200, label: 'Extra Light' },
  { value: 300, label: 'Light' },
  { value: 400, label: 'Regular' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semi Bold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'Extra Bold' },
  { value: 900, label: 'Black' },
];

export const PRESET_COLORS = [
  '#000000',
  '#374151',
  '#6B7280',
  '#9CA3AF',
  '#D1D5DB',
  '#F3F4F6',
  '#FFFFFF',
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#10B981',
  '#14B8A6',
  '#06B6D4',
  '#0EA5E9',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#A855F7',
  '#D946EF',
  '#EC4899',
  '#F43F5E',
];

export const EXTENDED_COLORS = [
  // Reds
  '#FEF2F2',
  '#FEE2E2',
  '#FECACA',
  '#FCA5A5',
  '#F87171',
  '#EF4444',
  '#DC2626',
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
  // Oranges
  '#FFF7ED',
  '#FFEDD5',
  '#FED7AA',
  '#FDBA74',
  '#FB923C',
  '#F97316',
  '#EA580C',
  '#C2410C',
  '#9A3412',
  '#7C2D12',
  // Yellows
  '#FEFCE8',
  '#FEF9C3',
  '#FEF08A',
  '#FDE047',
  '#FACC15',
  '#EAB308',
  '#CA8A04',
  '#A16207',
  '#854D0E',
  '#713F12',
  // Greens
  '#F0FDF4',
  '#DCFCE7',
  '#BBF7D0',
  '#86EFAC',
  '#4ADE80',
  '#22C55E',
  '#16A34A',
  '#15803D',
  '#166534',
  '#14532D',
  // Blues
  '#EFF6FF',
  '#DBEAFE',
  '#BFDBFE',
  '#93C5FD',
  '#60A5FA',
  '#3B82F6',
  '#2563EB',
  '#1D4ED8',
  '#1E40AF',
  '#1E3A8A',
  // Purples
  '#FAF5FF',
  '#F3E8FF',
  '#E9D5FF',
  '#D8B4FE',
  '#C084FC',
  '#A855F7',
  '#9333EA',
  '#7E22CE',
  '#6B21A8',
  '#581C87',
  // Pinks
  '#FDF2F8',
  '#FCE7F3',
  '#FBCFE8',
  '#F9A8D4',
  '#F472B6',
  '#EC4899',
  '#DB2777',
  '#BE185D',
  '#9D174D',
  '#831843',
  // Grays
  '#F9FAFB',
  '#F3F4F6',
  '#E5E7EB',
  '#D1D5DB',
  '#9CA3AF',
  '#6B7280',
  '#4B5563',
  '#374151',
  '#1F2937',
  '#111827',
];

export const ANIMATION_PRESETS: { value: string; label: string }[] = [
  { value: 'none', label: 'Brak' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'fadeInDown', label: 'Fade In Down' },
  { value: 'fadeInLeft', label: 'Fade In Left' },
  { value: 'fadeInRight', label: 'Fade In Right' },
  { value: 'slideInUp', label: 'Slide In Up' },
  { value: 'slideInDown', label: 'Slide In Down' },
  { value: 'slideInLeft', label: 'Slide In Left' },
  { value: 'slideInRight', label: 'Slide In Right' },
  { value: 'scaleIn', label: 'Scale In' },
  { value: 'bounceIn', label: 'Bounce In' },
  { value: 'rotateIn', label: 'Rotate In' },
  { value: 'flipIn', label: 'Flip In' },
];

export const EASING_OPTIONS: { value: string; label: string }[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In Out' },
  { value: 'spring', label: 'Spring' },
];

export const BLEND_MODES: { value: string; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'screen', label: 'Screen' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'darken', label: 'Darken' },
  { value: 'lighten', label: 'Lighten' },
];

export const BORDER_STYLES: { value: string; label: string }[] = [
  { value: 'none', label: 'Brak' },
  { value: 'solid', label: 'Ciągłe' },
  { value: 'dashed', label: 'Przerywane' },
  { value: 'dotted', label: 'Kropkowane' },
  { value: 'double', label: 'Podwójne' },
];

export const OVERFLOW_OPTIONS: { value: string; label: string }[] = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'scroll', label: 'Scroll' },
  { value: 'auto', label: 'Auto' },
];

// Old preset to pixel mappings for backward compatibility
export const PADDING_PRESET_MAP: Record<string, number> = {
  none: 0,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

export const MARGIN_PRESET_MAP: Record<string, number> = {
  none: 0,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

export const TEXT_SIZE_PRESET_MAP: Record<string, number> = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
};

export const FONT_WEIGHT_PRESET_MAP: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

export const BORDER_RADIUS_PRESET_MAP: Record<string, number> = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

export const SHADOW_PRESET_MAP: Record<
  string,
  { x: number; y: number; blur: number; spread: number; color: string; inset: boolean }
> = {
  sm: { x: 0, y: 1, blur: 2, spread: 0, color: 'rgba(0,0,0,0.05)', inset: false },
  md: { x: 0, y: 4, blur: 6, spread: -1, color: 'rgba(0,0,0,0.1)', inset: false },
  lg: { x: 0, y: 10, blur: 15, spread: -3, color: 'rgba(0,0,0,0.1)', inset: false },
  xl: { x: 0, y: 20, blur: 25, spread: -5, color: 'rgba(0,0,0,0.1)', inset: false },
};
